/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 5 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import { useTranslation, t, untranslated } from "@i18n";
import { Suspense, forwardRef, lazy, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { MutableRefObject, Ref } from "react";
import { portalMeasurementUnitsUrl } from "@Services/dataManager";
import axiosWrapper from "@Services/axiosWrapper";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import type { UpdateBulkQuantityAndUnitOfFileResponse, DatabankViewData } from "@ServerTypes/queries/queries.schema";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import AddToTakeoffButton from "../AddToTakeoffButton";
import {
  addMeasurementsToTakeoffsFromNodes,
  getQueryData,
  prepareEstimationFieldNeedToRefresh,
} from "@Actions/estimation";
import { setEstimateFieldsNeedtoRefresh } from "@Reducers/EstimateList";
import Views from "./Views";
import { RiBringToFront } from "react-icons/ri";
import { BiColorFill } from "react-icons/bi";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import ColorPickerComponent from "@CubeComponents/Mui_Form/ColorPickerComponent";
import { GrOverview } from "react-icons/gr";
import { AppDispatch, RootState } from "@Reducers/index";
import { useDispatch, useSelector } from "react-redux";
import { showToastNotification } from "@Reducers/toastSlice";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import { getFileversionIdAndNodeOffsetIdMap } from "@Services/cubeViewHelper";
import FileColumnsTreeView from "@Components/FileColumnsTreeView";
import _ from "lodash";
import { ExcelExportService } from "@slickgrid-universal/excel-export";
import { resetDatabank, updateCurrentDatabankView } from "@Reducers/Databank";
import Divider from "@mui/material/Divider";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import IconComponent from "@CubeComponents/icons";
import { Popover } from "@mui/material";
import {
  convertToDotNotation,
  extractPropertyPartialName,
  getFilteredColumns,
  getModifiedNodesList,
} from "@Features/viewer-modals/components/Modal/SearchSet/searchSetHelper";
import Text from "@CubeComponents/html/Text/Text";
import { getIsViewerDependencyLoaded } from "@Services/utils/helpers";
import { dispatchModelLoadingToast } from "@Actions/Viewer";
import { HoopsViewerContext, CubeGlobal } from "@Features/viewer-core";
import SlickGrid1, { typedColumns, toolbarItem } from "@CubeComponents/html/SlickGrid1";
import type { ColumnDefinition, ValidatedColumnDefinition, ToolbarItem } from "@CubeComponents/html/SlickGrid1";
import { setModifiedModelProps, updateQuantityAndUnit } from "@Reducers/Viewer";
import { Selection } from "@ts3d-hoops/web-viewer";
import { applyFilter } from "@Features/viewer-modals/utils/searchOperators";
import { convertDate } from "@Services/luxonHelper";
import { getModelPropertiesData } from "@Services/utils/modelPropertiesDataDB";
import { useModelPropertiesWiped } from "@Services/useModelPropertiesWiped";
import ModelDataClearedState from "@Features/design-viewer/components/Modals/ModelDataClearedState";
import type { ModelPropertyNode, ModelPropertiesData, ModelPropertyValue } from "@WebTypes/modelProperties";
import type { SlickgridReactInstance, SlickGrid, SlickDataView, Column, GridOption } from "slickgrid-react";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

const ColumnChooser = lazy(() => import("./ColumnChooser"));

/**
 * A grid row: a {@link ModelPropertyNode} flattened to dotted-notation keys
 * (e.g. "properties.Quantity") by `convertToDotNotation`, plus a stable `id`.
 *
 * Identity fields derive from the files_properties zod single source of truth
 * (ModelPropertyNode ← `filePropertyDocument`), so they stay in lock-step with
 * the backend. The string index signature holds the dynamic flattened property
 * keys (vendor-defined) and stays `any` so dotted-key reads (`row["properties.X"]`)
 * remain ergonomic for the grid/formatter code.
 */
type DatasetRow = {
  id: NonNullable<ModelPropertyNode["_id"]> | string;
  nodeId: NonNullable<ModelPropertyNode["nodeId"]>;
  fileVersionId: NonNullable<ModelPropertyNode["fileVersionId"]>;
  cubeUniqueId?: ModelPropertyNode["cubeUniqueId"];
  [dottedKey: string]: any;
};

type MeasurementUnit = NonNullable<GetMeasurementUnitsResponse["result"]>[number];

// Loose translation-fn shape used by the toolbar template components. The app's real
// `t` is strict-keyed; it is cast to this at the toolbar boundary (runtime-compatible —
// t accepts any string and returns string; only compile-time key checking is dropped).
type TFunc = (key: string) => string;
type QueryRule = Record<string, unknown> | null;
type FilterValue = { id: string } | null;
/** Saved-view payload applied to the grid (query/columns/grouping/conditions). */
type ViewChangePayload = {
  query: string;
  columns: string[];
  group: string[];
  sortConditions?: unknown[];
  filterConditions?: Record<string, unknown>;
  groupConditions?: unknown[];
};

/** SlickGrid1 instance + the `applyConditions` method the wrapper attaches at runtime. */
type GridInstance = SlickgridReactInstance & {
  applyConditions?: (data: {
    sortConditions: unknown[];
    filterConditions: unknown;
    groupConditions: unknown[];
  }) => void;
};

declare global {
  // Debug handles exposed on window for in-browser inspection of the grid/properties.

  var reactGridRef: MutableRefObject<GridInstance | null> | undefined;

  var modelPropertiesRef: MutableRefObject<ModelPropertyNode[]> | undefined;
}

type QuantificationTemplateProps = {
  columns: string[];
  t: TFunc;
  portalMeasurementsUnit: MutableRefObject<MeasurementUnit[]>;
  handleGlobalQuantityChange: (args: { field: string }) => void;
  handleGlobalUnitChange: (args: { value: string }) => void;
  handleGlobalSettingsUpdate: () => Promise<void>;
};

const QuantificationTemplate = ({
  columns,
  t,
  portalMeasurementsUnit,
  handleGlobalQuantityChange,
  handleGlobalUnitChange,
  handleGlobalSettingsUpdate,
}: QuantificationTemplateProps) => {
  const [anchorElQuantification, setAnchorElQuantification] = useState<HTMLElement | null>(null);
  const [isApplying, setIsApplying] = useState(false);
  const [isApplyEnabled, setIsApplyEnabled] = useState(false);
  const selectedQuantityRef = useRef("");
  const selectedUnitRef = useRef("");
  const urlSearchParams = new URLSearchParams(location.search);

  const onClose = () => {
    setAnchorElQuantification(null);
    setIsApplyEnabled(false);
    selectedQuantityRef.current = "";
    selectedUnitRef.current = "";
  };

  return (
    <>
      <ButtonComponent
        sx={{ flexShrink: 0 }}
        onClick={(e: React.MouseEvent) => setAnchorElQuantification(e.currentTarget as HTMLElement)}
        buttonType={BUTTON_TYPES.TEXT_BUTTON}
        startIcon={
          <div className="flex items-center justify-center w-6 h-6">
            <IconComponent name={"Quantification"} width="1.5rem" height={"1.5rem"} />
          </div>
        }
        endIcon={<ArrowDropDownIcon />}
        disabled={urlSearchParams.get("versionId")}
      >
        <Text>{t("Quantification")}</Text>
      </ButtonComponent>
      <Popover
        id={"popoverId"}
        open={Boolean(anchorElQuantification)}
        anchorEl={anchorElQuantification}
        onClose={onClose}
        sx={{
          "& .MuiPaper-root": {
            boxShadow: "0px 0.25rem 0.75rem rgba(0, 0, 0, 0.1)",
            borderRadius: "0.5rem",
            padding: "1.25rem",
            width: "26%",
            marginTop: "0.625rem",
            marginInlineStart: "0.625rem",
            zIndex: 9999999,
          },
        }}
      >
        <div className="w-full h-full flex flex-col">
          <span className="text-[#192048] text-base font-bold leading-normal mb-6">
            <Text>{t("Quantification")}</Text>
          </span>
          <span className="text-[color:var(--Text-text-primary,#192048)] text-sm font-semibold leading-[120%] mb-3">
            <Text>{t("Quantity Parameter")}</Text>
          </span>
          <span className="text-[color:var(--Text-text-secondary,#666A84)] font-source-sans-pro text-xs font-normal leading-[120%] mb-3">
            <Text sx={{ whiteSpace: "normal" }}>
              {t(
                "Specify the parameter using which the Quantity of the elements will be fetched. e.g. Volume, Area, Length etc.",
              )}
            </Text>
          </span>
          <FileColumnsTreeView
            change={(args: { field: string }) => {
              selectedQuantityRef.current = args.field;
              setIsApplyEnabled(!!(args.field || selectedUnitRef.current));
              handleGlobalQuantityChange(args);
            }}
            columns={columns}
          />
          <span className="text-[color:var(--Text-text-primary,#192048)] text-sm font-semibold leading-[120%] mb-3 mt-4">
            <Text>{t("Unit")}</Text>
          </span>
          <span className="text-[color:var(--Text-text-secondary,#666A84)] font-source-sans-pro text-xs font-normal leading-[120%] mb-3">
            <Text sx={{ whiteSpace: "normal" }}>
              {t(
                "The unit of the quantity fetched from above parameter. Please select the appropriate unit of measurement by referring to the properties in the metadata. Using the correct unit ensures accurate values and enables proper unit conversion within the takeoff.",
              )}
            </Text>
          </span>
          <AutoComplete
            options={portalMeasurementsUnit.current}
            valueField="_id"
            getOptionLabel={(option) => option?.nameWithSymbol || ""}
            onChange={(_event, newValue) => {
              // AutoComplete yields the selected MeasurementUnit; the ref/handler want its _id string.
              const value = newValue?._id || "";
              selectedUnitRef.current = value;
              setIsApplyEnabled(!!(value || selectedQuantityRef.current));
              handleGlobalUnitChange({ value });
            }}
            renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Unit")} />}
          />
          <div className="flex p-4 justify-center items-center gap-4 self-stretch rounded-lg bg-[rgba(255,207,85,0.25)] my-4">
            <span className="text-[color:var(--Text-text-primary,#192048)] font-source-sans-pro text-sm font-normal leading-[120%]">
              <Text sx={{ whiteSpace: "normal" }}>
                {t(
                  "This will override any pre-existing data in the Quantity and Unit columns. Takeoffs will remain unaffected.",
                )}
              </Text>
            </span>
          </div>
          <div className="w-[30%]">
            <ButtonComponent
              buttonType={BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={async () => {
                setIsApplying(true);
                await handleGlobalSettingsUpdate();
                setIsApplying(false);
                onClose();
              }}
              disabled={!isApplyEnabled || isApplying}
              loading={isApplying}
            >
              <Text>{t("Apply")}</Text>
            </ButtonComponent>
          </div>
        </div>
      </Popover>
    </>
  );
};

type FilterDropdownProps = {
  remoteDataSource: string;
  query: QueryRule;
  value: FilterValue | undefined;
  onChange: (args: { id: string; query: string } | null) => void;
  placeholder: string;
};
/** Saved-query option served by the queriesData remote source (matched on `id`). */
type QueryFilterOption = { id: string; query?: string; [key: string]: unknown };

const FilterDropdownComp = ({ remoteDataSource, query, value, onChange, placeholder }: FilterDropdownProps) => (
  <SelectDropDown<QueryFilterOption>
    remoteDataSource={remoteDataSource}
    query={query}
    value={value}
    onChange={onChange}
    buttonType="textButton"
    clearable
    popperWidth={200}
    placeholder={placeholder}
    fields={{
      text: "name",
      value: "id",
    }}
    {...({} as any)}
  />
);

type ViewsDropdownProps = {
  id: string;
  fileVersionId: string;
  onChange: (data: ViewChangePayload) => void;
};
const ViewsDropdownComp = ({ id, fileVersionId, onChange }: ViewsDropdownProps) => (
  <div className="flex-shrink-0 ms-2 me-4">
    {" "}
    <Views id={id} fileVersionId={fileVersionId} onChange={onChange} />{" "}
  </div>
);

type AddToTakeoffWrapProps = {
  onSuccess: (takeoffsChecked: ObjectIdString[]) => void | Promise<void>;
  estimateId: ObjectIdString | number;
  buttonRef: Ref<HTMLButtonElement>;
  onCreated: () => void;
};
const AddToTakeoffWrap = ({ onSuccess, estimateId, buttonRef, onCreated }: AddToTakeoffWrapProps) => (
  <div className="relative">
    {" "}
    <AddToTakeoffButton onSuccess={onSuccess} estimateId={estimateId} ref={buttonRef} onCreated={onCreated} />{" "}
  </div>
);

type ActionButtonsProps = {
  onSelectAll: () => void;
  onIsolateAll: () => void;
  onSetColorAll: () => void;
  colorValue: string;
  onColorChange: (color: { value: string }) => void;
};
const ActionButtons = ({ onSelectAll, onIsolateAll, onSetColorAll, colorValue, onColorChange }: ActionButtonsProps) => (
  <div className="relative z-0 flex items-center gap-4 px-4">
    {" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Select")}>
      {" "}
      <GrOverview size={18} onClick={onSelectAll} className="cursor-pointer" />
    </ToolTipComponent>{" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Isolate")}>
      {" "}
      <RiBringToFront size={18} onClick={() => onIsolateAll()} className="cursor-pointer" />{" "}
    </ToolTipComponent>{" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Color")}>
      {" "}
      <BiColorFill size={18} onClick={onSetColorAll} className="cursor-pointer" />{" "}
    </ToolTipComponent>{" "}
    <ToolTipComponent showTipPointer position="bottom" content={t("Modify Color")}>
      {" "}
      <ColorPickerComponent value={colorValue} change={onColorChange} enableOpacity={false} />{" "}
    </ToolTipComponent>{" "}
  </div>
);

type ColumnChooserBtnProps = { onClick: () => void };
const ColumnChooserBtn = ({ onClick }: ColumnChooserBtnProps) => (
  <ButtonComponent iconButton sx={{ flexShrink: 0 }} buttonType={BUTTON_TYPES.TEXT_BUTTON} onClick={onClick}>
    <IconComponent width={"1.5rem"} height={"1.5rem"} name="Column_Chooser" />
  </ButtonComponent>
);

const SeparatorLine = () => (
  <Divider orientation="vertical" variant="middle" sx={{ height: "2rem", background: "#D4E8FA" }} flexItem />
);

const createSeparatorItem = (): ToolbarItem => ({ id: "separator", template: SeparatorLine });

const ViewsDropdownItem = ({
  id,
  fileVersionId,
  onViewChange,
}: {
  id: string;
  fileVersionId: string;
  onViewChange: (data: ViewChangePayload) => void;
}) => <ViewsDropdownComp id={id} fileVersionId={fileVersionId} onChange={onViewChange} />;

const AddToTakeoffItem = ({
  handleAddMeasurementClick,
  estimateId,
  addToTakeoffButtonRef,
  onTakeoffButtonCreated,
}: {
  handleAddMeasurementClick: (takeoffsChecked: ObjectIdString[]) => void | Promise<void>;
  estimateId: ObjectIdString | number;
  addToTakeoffButtonRef: Ref<HTMLButtonElement>;
  onTakeoffButtonCreated: () => void;
}) => (
  <AddToTakeoffWrap
    onSuccess={handleAddMeasurementClick}
    estimateId={estimateId}
    buttonRef={addToTakeoffButtonRef}
    onCreated={onTakeoffButtonCreated}
  />
);

const QueryFilterItem = ({
  query,
  filterRef,
  handleQueryFilterChange,
  t,
}: {
  query: QueryRule;
  filterRef: MutableRefObject<{ value: FilterValue } | null>;
  handleQueryFilterChange: (args: { id: string; query: string } | null) => void;
  t: TFunc;
}) => (
  <FilterDropdownComp
    remoteDataSource={`${process.env.REACT_APP_CUBE_BACKEND}/queries/queriesData`}
    query={query}
    value={filterRef.current?.value}
    onChange={handleQueryFilterChange}
    placeholder={t("Filter by query")}
  />
);

const QuantificationItem = ({
  columns,
  t,
  portalMeasurementsUnit,
  handleGlobalQuantityChange,
  handleGlobalUnitChange,
  handleGlobalSettingsUpdate,
}: QuantificationTemplateProps) => (
  <QuantificationTemplate
    columns={columns}
    t={t}
    portalMeasurementsUnit={portalMeasurementsUnit}
    handleGlobalQuantityChange={handleGlobalQuantityChange}
    handleGlobalUnitChange={handleGlobalUnitChange}
    handleGlobalSettingsUpdate={handleGlobalSettingsUpdate}
  />
);

interface SearchSetProps {
  /**
   * Parent estimate id. Reaches this component through the viewer's untyped (`any`) prop chain
   * from the `estimateId` URL query param; a legacy numeric form is still coerced with `String()`
   * before it is sent, hence the union.
   */
  estimateId?: ObjectIdString | number;
  fileVersionId: ObjectIdString;
  allowGrouping?: boolean;
  disableAddMeasurementToTakeoff?: boolean;
  hideViews?: boolean;
  hideQueryFiltering?: boolean;
  hideExcludedClumns?: boolean;
  isFromQuery?: boolean;
  triggerIsolate?: boolean;
  setTriggerIsolate?: (value: boolean) => void;
  onSaveColumns?: (columns: string[]) => void;
  filterByQueryRule?: { query: QueryRule; columns?: string[] };
  filterQueryId?: string;
  enableAdditionalDataSelection?: boolean;
  id?: string;
}

const SearchSet = forwardRef<unknown, SearchSetProps>(
  ({
    estimateId,
    fileVersionId,
    allowGrouping = true,
    disableAddMeasurementToTakeoff = false,
    hideViews = false,
    hideQueryFiltering = false,
    hideExcludedClumns = false,
    isFromQuery = false,
    triggerIsolate = false,
    setTriggerIsolate,
    onSaveColumns,
    filterByQueryRule,
    id = "standaloneDatabank",
  }) => {
    const { t } = useTranslation();
    const [showShimmer, setShowShimmer] = useState(true);
    const filterRef = useRef<{ value: { id: string } | null } | null>(null);
    const [gridOptions, setGridOptions] = useState<Partial<GridOption> | undefined>(undefined);
    const [columnDefinitions, setColumnDefinitions] = useState<ValidatedColumnDefinition<DatasetRow>[] | null>(null);
    const [gridReady, setGridReady] = useState(false);
    const { estimateFieldsNeedToRefresh, HoopsModalStatus, modelProperties, estimateError, modifiedModelProps } =
      useSelector((state: RootState) => state);
    const isModelDataWiped = useModelPropertiesWiped(fileVersionId);
    // Merged model-properties (keyed [fileVersionId][nodeId]) held in a single
    // ref — no per-component useState copy. Cleared before each refetch so the
    // old snapshot is released before the next is built (caps peak heap). The
    // flattened list the query-filter needs is derived on demand from this ref.
    const modelDataRef = useRef<ModelPropertiesData>({});
    // Records the exact dataVersion produced by the databank's own quantity+unit
    // update (which patches the grid + ref locally). The version effect skips the
    // refetch for ONLY that version — a concurrent foreign bump has a different
    // version and still refetches. -1 = nothing to skip.
    const skipRefetchForVersionRef = useRef<number>(-1);
    // Latest committed modelProperties slice mirrored into a ref, so callbacks
    // read the current dataVersion off React state (no imperative store read).
    const modelPropertiesStateRef = useRef(modelProperties);
    modelPropertiesStateRef.current = modelProperties;
    const reactGridRef = useRef<GridInstance | null>(null);
    const dataviewObjRef = useRef<SlickDataView | null>(null);
    const gridObjRef = useRef<SlickGrid | null>(null);
    const _darkModeRef = useRef(false);
    const portalMeasurementsUnit = useRef<MeasurementUnit[]>([]);
    globalThis.reactGridRef = reactGridRef;
    const { isModelStructureReady, isSettingsApplied, viewerImpl } = useContext(HoopsViewerContext);
    const [dataset, setDataset] = useState<DatasetRow[]>([]);

    const reactGridReady = (reactGrid: SlickgridReactInstance) => {
      setGridReady(true);
      reactGridRef.current = reactGrid;
      gridObjRef.current = reactGrid.slickGrid;
      dataviewObjRef.current = reactGrid.dataView;
    };

    /** The grid rows currently selected (empty if the grid isn't mounted yet). */
    const getSelectedDataItems = (): DatasetRow[] => {
      const grid = reactGridRef.current?.slickGrid;
      if (!grid) return [];
      return grid.getSelectedRows().map((rowIdx: number) => grid.getDataItem(rowIdx) as DatasetRow);
    };

    const refreshDatasetFromModelData = (nextData: ModelPropertiesData) => {
      const refreshedById = new Map<DatasetRow["id"], DatasetRow>(
        (getModifiedNodesList(nextData) as DatasetRow[]).map((row) => [row.id, row]),
      );
      setDataset((prev) => prev.map((row) => refreshedById.get(row.id) ?? row));
    };

    useEffect(
      () => () => {
        const panel = document.querySelector<HTMLElement>(".panel-wm-content");
        panel?.classList.remove("dark-mode");
        const host = document.querySelector<HTMLElement>(`#${id}`);
        if (host) {
          host.dataset.bsTheme = "light";
        }
      },
      [id],
    );

    const getGridColumns = (extraColumns: string[] = []): ValidatedColumnDefinition<DatasetRow>[] => {
      // keep CUBE ID / CUBE Name pinned as fixed identity columns, but allow
      // Quantity / Unit (excluded constants) to be drag-reordered with the rest
      // of the dynamic columns so users can arrange the grid as they like
      let columns: ColumnDefinition<DatasetRow>[] = compulsoryColumnsConstant.map((column) => ({
        ...column,
        reorderable: false,
        groupable: false,
      }));
      if (!hideExcludedClumns) {
        columns = [
          ...columns,
          ...excludedColumnsConstant.map((column) => ({
            ...column,
            groupable: false,
          })),
        ];
      }
      columns = [
        ...columns,
        ...extraColumns.map((column): ColumnDefinition<DatasetRow> => {
          let columnName = extractPropertyPartialName(column);
          let label: string = column;
          let isFieldDatetime: boolean = false;

          if (column.startsWith("customProperties.")) {
            const columnData = modelProperties.questionIdAndNameMapper?.[columnName];
            if (columnData) {
              columnName = columnData.question;
              isFieldDatetime = columnData.type === "date-time";
            }
            label = `customProperties.${columnName}`;
          }
          return {
            minWidth: 150,
            field: column,
            id: column.replaceAll(".", ""),
            label,
            name: columnName,
            groupable: true,
            // dynamic columns must carry these flags on initial render so the
            // header menu shows filter/sort options and drag-to-reorder works
            // before any view is applied
            filterType: isFieldDatetime ? ("Date" as const) : ("Text" as const),
            filterable: true,
            sortable: true,
            reorderable: true,
            formatter: (_row, _cell, _value, _columnDef, dataContext) => {
              const fieldValue = _.get(dataContext, column);
              if (column.startsWith("customProperties.") && fieldValue) {
                if (isFieldDatetime) {
                  return convertDate(Number(fieldValue), "dateForTooltip");
                }
                if (Array.isArray(fieldValue)) {
                  return fieldValue.join(", ");
                }
                if (typeof fieldValue == "object") {
                  return `<a
                            href="${fieldValue.URL}"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-gradient_color cursor-pointer underline underline-offset-2"
                          >
                            ${fieldValue.displayText}
                          </a>`;
                }
              }
              return fieldValue;
            },
            excelExportOptions: {
              valueParserCallback: (_value, { dataContext }) => {
                if (_.get(dataContext, column) && typeof _.get(dataContext, column) == "object") {
                  return _.get(dataContext, column)["URL"];
                }
                return _.get(dataContext, column) || "";
              },
            },
          };
        }),
      ];
      return typedColumns<DatasetRow>(columns as Parameters<typeof typedColumns<DatasetRow>>[0]);
    };

    const excludedColumnsConstant: ColumnDefinition<DatasetRow>[] = [
      {
        id: "propertiesQuantity",
        field: "properties.Quantity",
        isFrozen: false,
        minWidth: 150,
        // `disableMenuOptions: true` suppressed the header menu and filter UI,
        // and `allowReordering: false` blocked drag-to-reorder
        filterType: "Number" as const,
        filterable: true,
        sortable: true,
        reorderable: true,
        label: extractPropertyPartialName("properties.Quantity"),
        name: extractPropertyPartialName("properties.Quantity"),
        excelExportOptions: {
          valueParserCallback: (_value, { dataContext }) => _.get(dataContext, "properties.Quantity") || "",
        },
      },
      {
        id: "propertiesUnit",
        field: "properties.Unit",
        isFrozen: false,
        minWidth: 150,
        // filter is text-based: _valueGetter below resolves the stored unit id
        // to its display label, which is what the user sees and filters by
        filterType: "Text" as const,
        filterable: true,
        sortable: true,
        reorderable: true,
        label: extractPropertyPartialName("properties.Unit"),

        name: extractPropertyPartialName("properties.Unit"),
        formatter: (_row, _cell, value) => {
          const unitData = portalMeasurementsUnit.current.find((data) => data._id == value);
          if (unitData) {
            return unitData.nameWithSymbol;
          }
          // legacy rows store the unit name itself instead of a unit id
          return value ?? "";
        },
        // resolves unit ids to display names so the filter options dropdown and
        // filter matching work on what the user sees, not raw unit ids; legacy
        // rows store plain unit names ("Count") — pass those through unchanged
        _valueGetter: (row) => {
          const rawUnit = _.get(row, "properties.Unit");
          const unitData = portalMeasurementsUnit.current.find((data) => data._id == rawUnit);
          return unitData ? unitData.nameWithSymbol : (rawUnit ?? "");
        },
        excelExportOptions: {
          valueParserCallback: (_value, { dataContext }) => {
            const rawUnit = _.get(dataContext, "properties.Unit");
            const unitData = portalMeasurementsUnit.current.find((data) => data._id == rawUnit);
            if (unitData) {
              return unitData.nameWithSymbol;
            }
            // legacy rows store the unit name itself instead of a unit id
            return rawUnit ?? "";
          },
        },
      },
    ];

    const compulsoryColumnsConstant: ColumnDefinition<DatasetRow>[] = [
      {
        field: "nodeId",
        id: "nodeId",
        name: t("CUBE ID"),
        disableMenuOptions: true,
        renderHeader: () => (
          <span className="font-semibold">
            <Text>{t("CUBE ID")}</Text>
          </span>
        ),
        width: 150,
        minWidth: 110,
        resizable: true,
        label: t("CUBE ID"),

        allowReordering: false,
        allowGrouping: false,
      },
      {
        id: "propertiesCUBE Name",
        field: "properties.CUBE Name",
        name: extractPropertyPartialName("properties.CUBE Name"),
        width: 200,
        minWidth: 150,
        resizable: true,
        label: extractPropertyPartialName("properties.CUBE Name"),
        disableMenuOptions: true,
        allowReordering: false,
        allowGrouping: false,
        excelExportOptions: {
          valueParserCallback: (_value, { dataContext }) => _.get(dataContext, "properties.CUBE Name") || "",
        },
      },
    ];

    const options: Partial<GridOption> = {
      autoResize: {
        container: `#${id}`,
        applyResizeToContainer: true,
        calculateAvailableSizeBy: "container",
      },
      showCustomFooter: true,
      enableHeaderMenu: false,
      enableSorting: true,
      enableColumnReorder: true,
      multiColumnSort: true,
      enableCheckboxSelector: true,
      // no enableAutoSizeColumns override: let SlickGrid1 size dynamically —
      // columns stretch to fill the panel when they fit (so the last column
      // ends at the grid edge like every other grid) and fall back to
      // horizontal scrolling when they overflow
      checkboxSelector: {
        toolTip: "",
      },
      draggableGrouping: {
        dropPlaceHolderText: "Drag a column header here to group its column",
        deleteIconCssClass: "mdi mdi-close text-color-danger",
        sortAscIconCssClass: "mdi mdi-arrow-up",
        sortDescIconCssClass: "mdi mdi-arrow-down",
      },
      darkMode: _darkModeRef.current,
      externalResources: [new ExcelExportService()],
      enableExcelExport: true,
    };

    useEffect(() => {
      if (!modifiedModelProps) return;
      if (modifiedModelProps.type !== "customMetadata") return;

      const { fileVersionId, nodeId, fieldsData } = modifiedModelProps;

      const nodeIds = Array.isArray(nodeId) ? nodeId : [nodeId];

      // clone current grid-backed data
      const nextData: any = { ...modelDataRef.current };

      nodeIds.forEach((id: number) => {
        const currNode = nextData[fileVersionId]?.[id];
        if (!currNode) return;

        const updatedCustomProperties = { ...(currNode.customProperties || {}) };

        if (Array.isArray(fieldsData.value) && fieldsData.value.length === 0) {
          delete updatedCustomProperties[fieldsData._id];
        } else {
          updatedCustomProperties[fieldsData._id] = fieldsData.value;
        }

        nextData[fileVersionId] = {
          ...nextData[fileVersionId],
          [id]: {
            ...currNode,
            customProperties: updatedCustomProperties,
            selectedFormTemplate: { _id: fieldsData.selectedFormTemplate },
          },
        };
      });

      refreshDatasetFromModelData(nextData);

      dispatch(setModifiedModelProps(null));
    }, [modifiedModelProps]);

    // Fetch the merged model-properties from IndexedDB whenever the version
    // the data lives in a single ref (no useState copy) and we drive the derived
    // grid state by hand, since ref writes don't trigger renders.
    useEffect(() => {
      if (!fileVersionId || !modelProperties.isLoaded) return;

      // The databank patches its own quantity+unit edit into the grid + ref
      // locally; skip the IDB re-read + full rebuild for ONLY the exact version
      // that edit produced (a concurrent foreign bump differs and still
      // refetches). Clear the loader too, in case a superseded in-flight fetch
      // left it on.
      if (skipRefetchForVersionRef.current === modelProperties.dataVersion) {
        skipRefetchForVersionRef.current = -1;
        setShowShimmer(false);
        return;
      }

      // Release the previous (possibly very large) snapshot BEFORE building the
      // next one, so peak heap stays ~1x; the loader covers the gap.
      setShowShimmer(true);
      modelDataRef.current = {};

      // Latest-wins guard: bumps across ticks start overlapping async reads that
      // can resolve out of order; only the most recent one may commit.
      let active = true;
      // getModelPropertiesData expands the merged file to its constituent
      // versions and resolves units/portalUnits/engineType internally from
      // `originalFileVersionId` — no store read or id resolution needed here.
      getModelPropertiesData({ originalFileVersionId: fileVersionId }).then((result: any) => {
        if (!active) return;
        modelDataRef.current = result;

        // Flatten every node to dot-notation rows ONCE, then reuse them for both
        // column detection and the grid dataset — avoids a second full
        // convertToDotNotation pass over the whole tree on each dataVersion bump.
        const rows = getModifiedNodesList(result) as DatasetRow[];

        const columnSet = new Set<string>();
        for (const row of rows) {
          for (const key of Object.keys(row)) {
            if (/^(properties|customProperties|typeProperties)\./.test(key)) {
              columnSet.add(key);
            }
          }
        }
        const filteredColumns = getFilteredColumns(Array.from(columnSet)) as string[];
        const gridColumns = getGridColumns(filteredColumns);

        setColumnDefinitions(gridColumns);
        initialiseToolbar(filteredColumns);
        setGridOptions(options);
        // Refetch: grid already mounted -> commit rows now. First load: gridReady
        // is still false; the effect below commits them once the grid mounts.
        if (modelProperties.isFileFormTemplatesLoaded && gridReady) {
          setDataset(rows);
          setShowShimmer(false);
        }
      });

      return () => {
        active = false;
      };
    }, [modelProperties.dataVersion, fileVersionId]);

    // Commit the built rows once the grid is mounted and form templates are
    // ready (first-load ordering: columns set -> grid mounts -> gridReady flips).
    useEffect(() => {
      if (modelProperties.isLoaded && modelProperties.isFileFormTemplatesLoaded && gridReady) {
        setShowShimmer(true);
        setDataset(getModifiedNodesList(modelDataRef.current) as DatasetRow[]);
        setShowShimmer(false);
      }
    }, [gridReady, modelProperties.isFileFormTemplatesLoaded]);

    const dispatch = useDispatch<AppDispatch>();

    const addToTakeoffButtonRef = useRef<HTMLButtonElement | null>(null);

    const [disableTakeoffButton, setDisableTakeoffButton] = useState(true);
    const [openColumnChooserModal, setOpenColumnChooserModal] = useState(false);
    const globalQuantityValue = useRef("");
    const globalUnitValue = useRef("");
    const [toolbar, setToolbar] = useState<ToolbarItem[]>([]);

    const isAllRowsSelectedRef = useRef(false);
    const nodeColor = useRef("#0000FF");

    const [query] = useState({
      params: {
        fileVersionId,
      },
      fileVersionId,
    });

    const {
      viewsLoaded,
      views = {},
      selectedView,
    } = useSelector((state: RootState) => state.Databank[id]?.[fileVersionId] || {});

    const viewsRef = useRef<Record<string, DatabankViewData>>({});
    const selectedViewRef = useRef<string>("");
    selectedViewRef.current = selectedView ?? "";
    // `views` is recursively typed (z.lazy) so it erases to Record<string, unknown>;
    // it is DatabankViewData at runtime.
    viewsRef.current = views as Record<string, DatabankViewData>;

    const onTakeoffButtonCreated = () => {
      if (addToTakeoffButtonRef.current) addToTakeoffButtonRef.current.disabled = true;
    };

    useEffect(() => {
      if (addToTakeoffButtonRef.current) {
        addToTakeoffButtonRef.current.disabled = !!disableTakeoffButton;
      }
    }, [disableTakeoffButton]);

    const getPortalMeasurementUnits = useCallback(async () => {
      const response = await axiosWrapper.post<GetMeasurementUnitsResponse>(portalMeasurementUnitsUrl, {});
      portalMeasurementsUnit.current = response.data.result ?? [];
    }, []);

    const fetchData = useCallback(async () => {
      await getPortalMeasurementUnits();
    }, [getPortalMeasurementUnits]);

    useEffect(() => {
      fetchData();
    }, [fetchData]);

    useEffect(() => {
      if (filterByQueryRule != undefined && gridReady && triggerIsolate) {
        if (
          getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })
        ) {
          (async () => {
            const records = await updateAllParameterOfGrid(filterByQueryRule.query, filterByQueryRule.columns);
            await handleIsolateOnInit(records);
            setTriggerIsolate?.(false);
          })();
        }
      } else {
        dispatch(dispatchModelLoadingToast());
      }
    }, [filterByQueryRule, gridReady, triggerIsolate]);

    const [enableWrapText, setEnableWrapText] = useState(false);

    const updateAllParameterOfGrid = async (query: QueryRule, columns: string[] = [], groupColumns: string[] = []) => {
      if (reactGridRef.current) {
        // Flattened complete node list for the filter, derived on demand from
        // the single keyed ref (no separate persistent flattened ref).
        const allNodes = Object.values(modelDataRef.current).flatMap((obj) => Object.values(obj));
        const { filteredData, allColumns } = applyFilter(allNodes, query);
        // `_id` is no longer synthesized by the IDB merge layer. Derive a
        // stable grid row id from `(fileVersionId, nodeId)` which is unique
        // within and across files.
        // convertToDotNotation flattens each node's own nodeId/fileVersionId into the row,
        // so every row satisfies DatasetRow's identity fields at runtime even though the
        // flatten is dynamically typed (its return is an open record).
        const convertedData = filteredData.map((data, index) => ({
          // nodes without _id would all share id:undefined and break the DataView
          id:
            data?.fileVersionId != null && data?.nodeId != null
              ? `${data.fileVersionId}_${data.nodeId}`
              : `node_${index}`,
          ...convertToDotNotation(data),
        })) as DatasetRow[];
        setDataset(convertedData);
        for (const column of columns) allColumns.add(column);
        for (const column of groupColumns) allColumns.add(column);
        // CUBE-25959: grid ref may not be mounted yet when initializeView races with grid setup
        const gridInstance = reactGridRef.current;
        if (!gridInstance?.slickGrid) return convertedData;
        const grid = gridInstance.slickGrid;
        const buttoncssClass = "mdi mdi-dots-vertical text-slate-900 inline-block  cursor-pointer mt-0";
        // Boundary with slickgrid's own Column type: getColumns()[0] is a raw slickgrid
        // Column and getGridColumns() are our ValidatedColumnDefinition; both are
        // structurally Column<DatasetRow> plus the custom `disableMenuOptions` flag.
        const sourceColumns = [grid.getColumns()[0], ...getGridColumns(Array.from(allColumns))] as Array<
          Column<DatasetRow> & { disableMenuOptions?: boolean }
        >;
        grid.setColumns(
          sourceColumns.map((column) => {
            if (column.disableMenuOptions) return column;

            let updatedColumn: Column<DatasetRow> = { ...column };
            updatedColumn.headerCssClass = (updatedColumn.headerCssClass || "") + " custom-header-column";

            const headerConfig = {
              buttons: [
                {
                  cssClass: buttoncssClass,
                  command: "toggle-highlight",
                  tooltip: column.id,
                },
              ],
            };
            updatedColumn = {
              ...updatedColumn,
              header: headerConfig,
              sortable: true,
              groupable: true,
              filterable: true,
              type: "string",
            } as Column<DatasetRow>;

            return updatedColumn;
          }),
        );
        gridInstance.resizerService.resizeColumnsByCellContent(true);
        requestAnimationFrame(() => {
          const grid = reactGridRef.current?.slickGrid;
          if (!grid) return; // CUBE-25959
          grid.resizeCanvas();
          grid.setOptions({ enableAutoSizeColumns: true });
          grid.autosizeColumns();
        });
        return convertedData;
      }
    };

    const getQueryResponse = async (id: string) => {
      const response = await getQueryData({ fileVersionId, id });
      const result = response.data.result;
      const first = Array.isArray(result) ? result[0] : undefined;
      if (response.data.status && first?.query) {
        return JSON.parse(first.query);
      }
      return null;
    };

    const onViewChange = (data: ViewChangePayload) => {
      initializeView(
        data.query,
        data.columns,
        data.group,
        data.sortConditions || [],
        data.filterConditions || {},
        data.groupConditions || [],
      );
    };

    const initializeView = async (
      query: string,
      columns: string[],
      group: string[],
      sortConditions: unknown[] = [],
      filterConditions: unknown = {},
      groupConditions: unknown[] = [],
    ) => {
      let queryResponse: QueryRule = {};
      if (query) {
        queryResponse = await getQueryResponse(query);
        filterRef.current = { value: { id: query } };
      } else {
        filterRef.current = { value: null };
      }
      updateAllParameterOfGrid(queryResponse, columns, group);
      reactGridRef.current?.applyConditions?.({ sortConditions, filterConditions, groupConditions });
    };

    useEffect(() => {
      if (viewsLoaded && gridReady) {
        if (selectedView && Object.hasOwn(views, selectedView)) {
          const v = (views as Record<string, DatabankViewData>)[selectedView];
          if (!v) return;
          initializeView(
            v.query ?? "",
            v.columns ?? [],
            v.group ?? [],
            v.sortConditions || [],
            v.filterConditions || {},
            v.groupConditions || [],
          );
        }
      }
    }, [viewsLoaded, gridReady]);

    useEffect(
      () => () => {
        dispatch(resetDatabank({ fileVersionId, id }));
      },
      [fileVersionId, id],
    );

    const handleQueryFilterChange = async (args: { id: string; query: string } | null) => {
      if (args) {
        filterRef.current = { value: { id: args.id } };
        const queryData = JSON.parse(args.query) as QueryRule;
        const value = args.id;
        const columns = await getDistinctFields(queryData);
        updateAllParameterOfGrid(queryData, columns, viewsRef.current[selectedViewRef.current]?.group);
        dispatch(
          updateCurrentDatabankView({
            data: {
              query: value,
              columns,
            },
            fileVersionId,
            id,
          }),
        );
      } else {
        updateAllParameterOfGrid(
          {},
          viewsRef.current[selectedViewRef.current]?.columns || [],
          viewsRef.current[selectedViewRef.current]?.group,
        );
        filterRef.current = { value: null };
        dispatch(
          updateCurrentDatabankView({
            data: {
              query: "",
            },
            fileVersionId,
            id,
          }),
        );
      }
    };

    const getDistinctFields = async (query: QueryRule): Promise<string[]> => {
      const fields = new Set<string>();
      const extractFields = (q: Record<string, any> | null | undefined) => {
        if (!q || typeof q !== "object" || Object.keys(q).length === 0) {
          return;
        }
        if (Object.hasOwn(q, "condition")) {
          for (const rule of q.rules) {
            extractFields(rule);
          }
        } else if (Object.hasOwn(q, "field")) {
          fields.add(q.field);
        }
      };
      extractFields(query);
      return Array.from(fields);
    };

    const handleAddMeasurementClick = async (takeoffsChecked: ObjectIdString[]) => {
      const selectedRecords = getSelectedDataItems();
      const recordWithoutUnitAndQuantity = selectedRecords.find((record) => {
        const quantity = record["properties.Quantity"];
        const unit = record["properties.Unit"];
        if (!quantity || !unit) {
          return true;
        }
      });
      if (recordWithoutUnitAndQuantity) {
        dispatch(
          showToastNotification({
            content: t("Quantity and Unit should not be empty!"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      } else {
        try {
          // `estimateId` can still arrive as a legacy number through the viewer's untyped (`any`)
          // prop chain, so the pre-existing String() coercion is kept and the id is adopted once
          // here, at its declaration.
          const estimateObjectId = estimateId == null ? undefined : (String(estimateId) as ObjectIdString);
          // The Add-to-takeoff control only renders when estimateId is set (toolbar gating).
          if (estimateObjectId == null) return;
          const response = await addMeasurementsToTakeoffsFromNodes({
            nodes: isAllRowsSelectedRef.current
              ? []
              : selectedRecords.map(({ nodeId, fileVersionId }) => ({
                  nodeId,
                  fileVersionId,
                })),
            filterQuerySet: viewsRef.current[selectedViewRef.current]?.query,
            fileVersionId,
            estimateId: estimateObjectId,
            takeoffId: takeoffsChecked,
          });
          if (response.data.status) {
            const setData = prepareEstimationFieldNeedToRefresh({
              HoopsModalStatus,
              measurements: true,
              takeoffs: takeoffsChecked,
              estimateFieldsNeedToRefresh,
            });
            dispatch(setEstimateFieldsNeedtoRefresh(setData));
            dispatch(
              showToastNotification({
                content: response.data.message
                  ? untranslated(response.data.message)
                  : t("Measurement attached to takeoff successfully."),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
          }
        } catch (e: unknown) {
          dispatch(
            showToastNotification({
              content: SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            }),
          );
          console.log(e);
          Promise.reject(e);
        }
      }
    };

    const handleSelectCallback = (selectedRecords: DatasetRow[]) => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      viewerImpl?.hwv?.selectionManager?.clear();
      const selectedNodes: Selection.SelectionItem[] = [];
      for (const nodeData of selectedRecords) {
        try {
          selectedNodes.push(Selection.SelectionItem.create(nodeData.nodeId + mapper[nodeData.fileVersionId]));
        } catch (e: unknown) {
          console.log(e);
          Promise.reject(e);
        }
      }
      viewerImpl?.hwv?.selectionManager?.add(selectedNodes, false);
    };

    const onSelectedRowsChanged = (args: { detail: { args: { rows: number[] } } }) => {
      const grid = reactGridRef.current?.slickGrid;
      const selectedRecords = grid ? args.detail.args.rows.map((rowIdx) => grid.getDataItem(rowIdx) as DatasetRow) : [];
      if (selectedRecords.length >= 1) {
        setDisableTakeoffButton(false);
      } else {
        setDisableTakeoffButton(true);
      }
    };

    const handleSelectAllClick = () => {
      handleSelectCallback(getSelectedDataItems());
    };

    const handleIsolateOnInit = async (allRecords?: DatasetRow[]) => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      if (allRecords?.length) {
        handleSelectCallback(allRecords);
        await CubeGlobal.isolateNodes(allRecords.map((data) => data.nodeId + mapper[data.fileVersionId]));
      }
    };

    const handleIsolateAllClick = async () => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const selectedRecords = getSelectedDataItems();
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      if (selectedRecords.length) {
        handleSelectAllClick();
        await CubeGlobal.isolateNodes(selectedRecords.map((data) => data.nodeId + mapper[data.fileVersionId]));
      }
    };

    const onColorChange = (color: { value: string }) => (nodeColor.current = color.value);

    const handleSetColorAll = async () => {
      if (!getIsViewerDependencyLoaded({ viewerImpl, isModelStructureReady, isSettingsApplied, hwv: CubeGlobal.hwv })) {
        dispatch(dispatchModelLoadingToast());
        return;
      }
      const selectedRecords = getSelectedDataItems();
      const mapper = getFileversionIdAndNodeOffsetIdMap(fileVersionId);
      for (const nodeData of selectedRecords) {
        try {
          await CubeGlobal.setColorFunc(nodeData.nodeId + mapper[nodeData.fileVersionId], nodeColor.current);
        } catch (e: unknown) {
          console.log(e);
          Promise.reject(e);
        }
      }
    };

    const buildViewsToolbarItems = (): ToolbarItem[] =>
      hideViews
        ? []
        : [
            toolbarItem({
              template: ViewsDropdownItem,
              props: { getCurrentColumnsState, id, fileVersionId, onViewChange },
              id: "views",
            }),
          ];

    const buildAddToTakeoffItems = (): ToolbarItem[] =>
      disableAddMeasurementToTakeoff || estimateId == null
        ? []
        : [
            toolbarItem({
              template: AddToTakeoffItem,
              props: { handleAddMeasurementClick, estimateId, addToTakeoffButtonRef, onTakeoffButtonCreated },
            }),
            createSeparatorItem(),
          ];

    const buildQueryFilterItems = (): ToolbarItem[] =>
      hideQueryFiltering
        ? []
        : [
            toolbarItem({
              template: QueryFilterItem,
              props: { query, filterRef, handleQueryFilterChange, t: t as TFunc },
            }),
            createSeparatorItem(),
          ];

    const buildQuantificationItems = (columns: string[]): ToolbarItem[] =>
      isFromQuery
        ? []
        : [
            toolbarItem({
              id: "quantification",
              template: QuantificationItem,
              props: {
                columns,
                t: t as TFunc,
                portalMeasurementsUnit,
                handleGlobalQuantityChange,
                handleGlobalUnitChange,
                handleGlobalSettingsUpdate,
              },
            }),
            createSeparatorItem(),
          ];

    const initialiseToolbar = (columns: string[]) => {
      const items: ToolbarItem[] = [
        ...buildViewsToolbarItems(),
        createSeparatorItem(),
        ...(!estimateId || estimateError ? [] : buildAddToTakeoffItems()),
        ...buildQueryFilterItems(),
        toolbarItem({
          template: ActionButtons,
          props: {
            onSelectAll: handleSelectAllClick,
            onIsolateAll: handleIsolateAllClick,
            onSetColorAll: handleSetColorAll,
            colorValue: nodeColor.current,
            onColorChange,
          },
        }),
        createSeparatorItem(),
        ...(!estimateId || estimateError ? [] : buildQuantificationItems(columns)),
        toolbarItem({
          template: ColumnChooserBtn,
          props: { onClick: handleOpenColumnChooserModal },
          id: "columnChoose",
          rightAllign: true,
          sticky: true,
        }),
      ];
      setToolbar(items);
    };

    const applyLocalQuantityUnitUpdates = (
      nextData: ModelPropertiesData,
      records: DatasetRow[],
    ): ModelPropertiesData => {
      for (const row of records) {
        const { fileVersionId, nodeId } = row;
        const fileMap: Record<number, ModelPropertyNode> = nextData[fileVersionId]
          ? { ...nextData[fileVersionId] }
          : {};
        nextData[fileVersionId] = fileMap;
        // Fresh node is populated incrementally below; the empty literal is a partial node.
        const currNode: ModelPropertyNode = fileMap[nodeId] ? { ...fileMap[nodeId] } : ({} as ModelPropertyNode);
        fileMap[nodeId] = currNode;
        const props: Record<string, ModelPropertyValue> = currNode.properties ? { ...currNode.properties } : {};
        if (globalUnitValue.current) {
          props.Unit = globalUnitValue.current || "";
        }
        if (globalQuantityValue.current) {
          const qty: number | string =
            Number.parseFloat(_.get(currNode, globalQuantityValue.current)) ||
            Number.parseFloat(_.get(row, globalQuantityValue.current)) ||
            "";
          props.Quantity = qty;
        }
        currNode.properties = props;
      }
      return nextData;
    };

    const handleGlobalSettingsUpdate = async () => {
      const selectedRecords = getSelectedDataItems();

      if (!selectedRecords.length) {
        dispatch(
          showToastNotification({
            content: t("No row selected"),
            type: TOAST_TYPE_ERROR,
          }),
        );
        return;
      }

      try {
        // The /queries/updateBulkQuantityAndUnitOfFile request body sends an open `params`
        // array (not modelled by updateBulkQuantityAndUnitOfFileRequest, which is a looseObject).
        const updateParams: Record<string, unknown>[] = [];
        const quantityUpdates: Array<{
          fileVersionId: string;
          nodeId: number;
          unit: string;
          quantity: string | number;
        }> = [];

        selectedRecords.forEach((data) => {
          const nodeMetadata = modelDataRef.current?.[data.fileVersionId]?.[data.nodeId];
          // Guard the in-flight refetch window: modelDataRef may be {} (released
          // before a concurrent refetch) while the old rows are still selectable
          // in the grid. Skip rows we can't resolve rather than dereferencing
          // undefined (parentMap/childrenMap below).
          if (!nodeMetadata) return;

          updateParams.push({
            fileVersionId: data.fileVersionId,
            fileId: data.fileId,
            projectId: data.projectId,
            quantity: _.get(data, globalQuantityValue.current) || "",
            unit: globalUnitValue.current || "",
            estimateId,
            parentMap: nodeMetadata.parentMap,
            childrenMap: nodeMetadata.childrenMap,
            parent: nodeMetadata.parent,
            portalId: nodeMetadata.portalId,
            nodeId: data.nodeId,
          });
          quantityUpdates.push({
            fileVersionId: data.fileVersionId,
            nodeId: data.nodeId,
            unit: globalUnitValue.current,
            quantity: _.get(modelDataRef.current[data.fileVersionId]?.[data.nodeId], globalQuantityValue.current) || "",
          });
        });
        // Predict the version this edit's bump will produce (current + 1) from
        // the latest committed modelProperties, and skip the refetch for only
        // that version (the edit is applied to the grid + ref locally below).
        // A foreign bump interleaving in the dispatch window is a rare exception
        // that would consume this prediction — acceptable, since a quantity edit
        // and a cross-panel edit are seldom simultaneous, and a missed skip just
        // costs a harmless refetch.
        skipRefetchForVersionRef.current = modelPropertiesStateRef.current.dataVersion + 1;
        // Single dispatch for the whole batch — `dataVersion` bumps once, not per record.
        dispatch(updateQuantityAndUnit(quantityUpdates));

        await axiosWrapper.post<UpdateBulkQuantityAndUnitOfFileResponse>(
          `${getBackendUrl()}/queries/updateBulkQuantityAndUnitOfFile`,
          { params: updateParams },
        );

        const nextData = applyLocalQuantityUnitUpdates({ ...modelDataRef.current }, selectedRecords);
        // Write the optimistic edit back into the ref (we skipped the refetch),
        // so later node lookups and the on-demand filter list stay consistent.
        modelDataRef.current = nextData;
        refreshDatasetFromModelData(nextData);

        dispatch(
          showToastNotification({
            content: t("Data updated successfully"),
            type: TOAST_TYPE_SUCCESS,
          }),
        );
      } catch (e) {
        // The local patch may not have run; un-skip so the bump's refetch (if it
        // hasn't already fired) reconciles the grid with the persisted IDB state.
        skipRefetchForVersionRef.current = -1;
        console.log(e);
        dispatch(
          showToastNotification({
            content: SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
        Promise.reject(e);
      }
    };

    const handleSaveColumns = (data: string[]) => {
      const gridInstance = reactGridRef.current;
      if (gridInstance?.slickGrid) {
        // setColumns is SlickGrid's raw API (slickgrid `Column`); our typed column
        // defs are structurally compatible at runtime, so bridge across the boundary.
        gridInstance.slickGrid.setColumns([
          gridInstance.slickGrid.getColumns()[0],
          ...getGridColumns(data),
        ] as Column<DatasetRow>[]);
        gridInstance.resizerService.resizeColumnsByCellContent(true);
      }
      if (!hideViews) {
        dispatch(
          updateCurrentDatabankView({
            data: {
              columns: data,
            },
            fileVersionId,
            id,
          }),
        );
      }
      handleOpenColumnChooserModal();
      if (onSaveColumns) {
        onSaveColumns(data);
      }
    };

    const handleOpenColumnChooserModal = () => {
      setOpenColumnChooserModal(!openColumnChooserModal);
    };

    const handleGlobalQuantityChange = (args: { field: string }) => {
      globalQuantityValue.current = args.field;
    };

    const handleGlobalUnitChange = (args: { value: string }) => {
      globalUnitValue.current = args.value;
    };

    const getCurrentColumnsState = () =>
      getFilteredColumns((reactGridRef.current?.slickGrid?.getColumns() ?? []).slice(1).map((col) => col.field));

    const onColumnsReordered = () => {
      const columns = (reactGridRef.current?.slickGrid?.getColumns() ?? [])
        .filter(
          (column, index) =>
            index != 0 &&
            !excludedColumnsConstant.some((data) => data.field == column.field) &&
            !compulsoryColumnsConstant.some((data) => data.field == column.field),
        )
        .map((column) => column.field);
      if (onSaveColumns) {
        onSaveColumns(columns);
      }
      if (!isFromQuery) {
        dispatch(
          updateCurrentDatabankView({
            data: {
              columns,
            },
            fileVersionId,
            id,
          }),
        );
      }
    };

    const handleConditionsChange = ({
      sortConditions,
      filterConditions,
      groupConditions,
    }: {
      sortConditions: NonNullable<DatabankViewData["sortConditions"]>;
      filterConditions: NonNullable<DatabankViewData["filterConditions"]>;
      groupConditions: NonNullable<DatabankViewData["groupConditions"]>;
    }) => {
      if (!hideViews) {
        dispatch(
          updateCurrentDatabankView({
            data: { sortConditions, filterConditions, groupConditions },
            fileVersionId,
            id,
          }),
        );
      }
    };

    // Called by SlickGrid1 when a column is hidden/shown via the column header dropdown
    const handleGridSaveColumns = () => {
      const columns = reactGridRef.current?.slickGrid
        ?.getColumns()
        ?.filter(
          (col, idx) =>
            idx !== 0 &&
            !excludedColumnsConstant.some((e) => e.field === col.field) &&
            !compulsoryColumnsConstant.some((e) => e.field === col.field),
        )
        ?.map((col) => col.field);
      if (columns && !hideViews) {
        dispatch(updateCurrentDatabankView({ data: { columns }, fileVersionId, id }));
      }
    };

    if (isModelDataWiped) return <ModelDataClearedState />;

    return (
      <div className={` ${isFromQuery ? "h-[25rem]" : "h-full"} flex flex-col`}>
        {openColumnChooserModal && (
          <Suspense>
            <ColumnChooser
              handleOpenColumnChooserModal={handleOpenColumnChooserModal}
              handleSaveColumns={handleSaveColumns}
              data={getFilteredColumns(
                (columnDefinitions ?? []).slice(1).map((data) => ({
                  field: data.field,
                  label: data.label,
                })),
              )}
              selectedData={getCurrentColumnsState()}
            />
          </Suspense>
        )}
        {gridOptions && columnDefinitions && (
          <div className="h-full w-full flex flex-col">
            <div id={id} className="h-full w-full overflow-hidden">
              <SlickGrid1
                gridId={`${id}_grid18`}
                showLoader={showShimmer}
                toolbar={toolbar}
                ref={reactGridRef}
                toolbarHeight={16}
                enableWrapText={enableWrapText}
                dataset={dataset}
                setEnableWrapText={setEnableWrapText}
                columnDefinitions={columnDefinitions}
                gridOptions={gridOptions}
                showPageCount={false}
                onColumnsReordered={onColumnsReordered}
                excelExport={true}
                enableGroupingOption={allowGrouping}
                enableRowResize={true}
                enableFilteringOption
                hasCustomColumnChooser
                enableSortingOption
                onReactGridCreated={($event) => reactGridReady($event)}
                onSelectedRowsChanged={onSelectedRowsChanged}
                onConditionsChange={handleConditionsChange}
                onSaveColumns={handleGridSaveColumns}
              />
            </div>
          </div>
        )}
      </div>
    );
  },
);

SearchSet.displayName = "SearchSet";
export default SearchSet;
